import type { ChangeEvent, ReactNode, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useFetcher, useNavigate, useSearchParams } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { AppraisalType } from '@/data/appraisal';
import {
  APPRAISAL_DISCLAIMER,
  appraisalTypes,
  contactMethods,
  contactTimes,
  propertyTypes,
  sellingReasons,
  timeframes,
  wizardSteps,
} from '@/data/appraisal';
import { clearDraft, loadDraft, saveDraft } from '@/features/appraisal/draft-storage';
import { appraisalPayloadSchema, createEmptyDraft, draftToPayload } from '@/features/appraisal/schema';
import type { AppraisalDraft, Photo } from '@/features/appraisal/schema';

import type { GooglePlacePrediction } from './use-google-maps';
import { useGoogleMaps } from './use-google-maps';

type Errors = Record<string, string>;

/** Client-side photo limits (mirrored by the route action once it lands). */
const MAX_PHOTOS = 10;
const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB

/** Maps a zod issue path to the wizard step index that owns that field. */
function pathToStep(path: string): number {
  if (path.startsWith('property')) {
    if (path.endsWith('address')) return 0;
    return 1; // propertyType, bedrooms, bathrooms, carSpaces, landSizeSqm
  }
  if (path.startsWith('timeline')) return 2;
  if (path.startsWith('notes') || path.startsWith('photos')) return 3;
  if (path.startsWith('contact')) return 4;
  return 5;
}

function validateDraft(draft: AppraisalDraft): Errors {
  const result = appraisalPayloadSchema.safeParse(draftToPayload(draft));
  if (result.success) return {};
  const errors: Errors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.');
    if (!errors[key]) errors[key] = issue.message;
  }
  return errors;
}

function Field({
  label,
  htmlFor,
  error,
  optional,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium" htmlFor={htmlFor}>
        {label} {optional ? <span className="text-muted-foreground font-normal">(optional)</span> : null}
      </label>
      {children}
      {error ? <p className="text-destructive mt-1 text-xs">{error}</p> : null}
    </div>
  );
}

function OptionCard({
  name,
  checked,
  onSelect,
  title,
  description,
}: {
  name: string;
  checked: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer flex-col gap-1.5 rounded-lg border p-5 transition-colors',
        checked ? 'border-primary ring-primary ring-2' : 'border-input hover:border-primary/60',
      )}
    >
      <span className="flex items-center gap-2">
        <input type="radio" name={name} className="accent-primary" checked={checked} onChange={onSelect} />
        <span className="text-sm font-semibold">{title}</span>
      </span>
      <span className="text-muted-foreground text-xs">{description}</span>
    </label>
  );
}

export function AppraisalWizard() {
  const fetcher = useFetcher<{ ok?: boolean; error?: string }>();
  const submitted = fetcher.data?.ok === true;
  const submitting = fetcher.state !== 'idle';

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The current step is derived from the URL (`?step=<id>`) so it survives
  // refresh and works with the browser back/forward buttons. Unknown/missing
  // values fall back to the first step.
  const stepFromUrl = wizardSteps.findIndex((s) => s.id === searchParams.get('step'));
  const step = stepFromUrl < 0 ? 0 : stepFromUrl;

  const [draft, setDraft] = useState<AppraisalDraft>(createEmptyDraft);
  // Actual photo File objects to upload. Kept out of the draft because the draft
  // is serialized to localStorage (which can't hold file bytes); the draft keeps
  // only the {name,size,type} metadata for display.
  const [photoFiles, setPhotoFiles] = useState<Array<File>>([]);
  const [previewMap, setPreviewMap] = useState<Map<File, string>>(new Map());
  const [errors, setErrors] = useState<Errors>({});
  // Steps the user has already tried to advance past — errors are only shown for
  // these, so a freshly-opened step never flags its empty fields as invalid.
  const [validatedSteps, setValidatedSteps] = useState<ReadonlySet<number>>(() => new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Counts forward navigations this session so "Back" can use the browser
  // history when possible, without leaving the site on a direct deep-link.
  const forwardCount = useRef(0);
  // Skip persisting the very first (empty) render so we don't clobber a
  // just-restored draft before the load effect runs.
  const skipSave = useRef(true);

  // Restore any persisted draft after mount (client only — avoids SSR
  // hydration mismatches).
  useEffect(() => {
    const stored = loadDraft();
    if (stored) setDraft(stored);
  }, []);

  // Persist the draft on every change (best-effort, debounce-free is fine here).
  useEffect(() => {
    if (skipSave.current) {
      skipSave.current = false;
      return;
    }
    saveDraft(draft);
  }, [draft]);

  // Clear persisted data once the request has been submitted successfully.
  useEffect(() => {
    if (submitted) clearDraft();
  }, [submitted]);

  // Build/revoke object URLs for photo thumbnails as the selection changes.
  // Keyed by File identity (not index) so removing one photo doesn't churn or
  // momentarily misalign the thumbnails still on screen. The last map is kept
  // in a ref purely so the unmount effect below can revoke whatever is current.
  const previewMapRef = useRef<Map<File, string>>(new Map());
  useEffect(() => {
    setPreviewMap((prev) => {
      const next = new Map<File, string>();
      for (const file of photoFiles) {
        next.set(file, prev.get(file) ?? URL.createObjectURL(file));
      }
      for (const [file, url] of prev) {
        if (!next.has(file)) URL.revokeObjectURL(url);
      }
      previewMapRef.current = next;
      return next;
    });
  }, [photoFiles]);

  // Revoke every remaining preview URL on unmount.
  useEffect(() => {
    return () => {
      previewMapRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  // Only surface validation errors for steps the user has already attempted to
  // leave — so advancing to a fresh step doesn't immediately flag its still-empty
  // fields as invalid.
  const stepErrors = useMemo(() => {
    if (!validatedSteps.has(step)) return {};
    const filtered: Errors = {};
    for (const [key, message] of Object.entries(errors)) {
      if (pathToStep(key) === step) filtered[key] = message;
    }
    return filtered;
  }, [errors, step, validatedSteps]);

  const isLastStep = step === wizardSteps.length - 1;

  // ----- draft mutation helpers -----
  const updateProperty = (patch: Partial<AppraisalDraft['property']>) => {
    setDraft((d) => ({ ...d, property: { ...d.property, ...patch } }));
  };

  const updateTimeline = (patch: Partial<AppraisalDraft['timeline']>) => {
    setDraft((d) => ({ ...d, timeline: { ...d.timeline, ...patch } }));
  };

  const updateContact = (patch: Partial<AppraisalDraft['contact']>) => {
    setDraft((d) => ({ ...d, contact: { ...d.contact, ...patch } }));
  };

  // ----- navigation -----
  const goToStep = (index: number, options?: { replace?: boolean }) => {
    const clamped = Math.min(Math.max(index, 0), wizardSteps.length - 1);
    const target = wizardSteps[clamped];
    if (!target) return;
    const next = new URLSearchParams(searchParams);
    next.set('step', target.id);
    // Keep the form exactly where it is on screen — without this, React
    // Router's default scroll-to-top would jump the page on every step change.
    setSearchParams(next, { replace: options?.replace, preventScrollReset: true });
  };

  const goNext = () => {
    const allErrors = validateDraft(draft);
    setErrors(allErrors);
    // Mark the current step as attempted so its errors (if any) become visible.
    setValidatedSteps((prev) => new Set(prev).add(step));
    const hasStepError = Object.keys(allErrors).some((key) => pathToStep(key) === step);
    if (hasStepError) return;
    forwardCount.current += 1;
    goToStep(step + 1);
  };

  const goBack = () => {
    // Prefer real browser history so back/forward stay in sync; fall back to a
    // URL replace when we have no in-app forward history (e.g. direct deep-link).
    if (forwardCount.current > 0) {
      forwardCount.current -= 1;
      void navigate(-1);
    } else {
      goToStep(step - 1, { replace: true });
    }
  };

  // Selecting photos updates both the File list (uploaded on submit, once the
  // action supports it) and the draft metadata (shown in the UI + review step).
  // Oversized files are dropped and the count is capped.
  const handlePhotosSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const accepted = files.filter((f) => f.size <= MAX_PHOTO_BYTES).slice(0, MAX_PHOTOS);
    setPhotoFiles(accepted);
    setDraft((d) => ({ ...d, photos: accepted.map((f): Photo => ({ name: f.name, size: f.size, type: f.type })) }));
  };

  const removePhoto = (index: number) => {
    setPhotoFiles((files) => files.filter((_, i) => i !== index));
    setDraft((d) => ({ ...d, photos: d.photos.filter((_, i) => i !== index) }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = () => {
    const allErrors = validateDraft(draft);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      // Reveal errors on every step that has one, and jump to the first.
      const errorSteps = Object.keys(allErrors).map(pathToStep);
      setValidatedSteps((prev) => new Set([...prev, ...errorSteps]));
      goToStep(Math.min(...errorSteps), { replace: true });
      return;
    }
    const payload = draftToPayload(draft);
    const formData = new FormData();
    formData.set('payload', JSON.stringify(payload));
    for (const file of photoFiles) {
      formData.append('photos', file);
    }
    void fetcher.submit(formData, { method: 'post', encType: 'multipart/form-data' });
  };

  return (
    <div className="card--light form-light">
      {submitted ? (
        <Confirmation />
      ) : (
        <>
          <div className="mb-6">
            <p className="text-eyebrow">Free Appraisal</p>
            <h1 className="mt-1 text-xl font-semibold">Request your property appraisal</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Tell us about your property and Lucio will be in touch with an honest, no-obligation appraisal.
            </p>
          </div>

          <StepIndicator current={step} />

          <div className="mt-8">
            <h2 className="mb-6 text-2xl font-semibold">{wizardSteps[step]?.title}</h2>

            {step === 0 ? <PropertyStep draft={draft} errors={stepErrors} onChange={updateProperty} /> : null}
            {step === 1 ? <DetailsStep draft={draft} errors={stepErrors} onChange={updateProperty} /> : null}
            {step === 2 ? <TimelineStep draft={draft} errors={stepErrors} onChange={updateTimeline} /> : null}
            {step === 3 ? (
              <MoreInfoStep
                draft={draft}
                photoFiles={photoFiles}
                previewMap={previewMap}
                fileInputRef={fileInputRef}
                onNotesChange={(notes) => setDraft((d) => ({ ...d, notes }))}
                onPhotosSelected={handlePhotosSelected}
                onRemovePhoto={removePhoto}
              />
            ) : null}
            {step === 4 ? <ContactStep draft={draft} errors={stepErrors} onChange={updateContact} /> : null}
            {step === 5 ? <ReviewStep draft={draft} onEditStep={goToStep} /> : null}
          </div>

          <div className="border-input mt-12 flex flex-col-reverse gap-3 border-t pt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            {step > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="text-muted-foreground hover:text-foreground inline-flex w-full items-center justify-center gap-1 py-2 text-sm font-medium transition-colors sm:w-auto sm:justify-start sm:py-0"
              >
                <Icon name="heroicons/chevron-left-micro" className="size-4" /> Back
              </button>
            ) : (
              <span />
            )}

            {isLastStep ? (
              <Button size="lg" onClick={handleSubmit} disabled={submitting} className="w-full sm:w-auto">
                {submitting ? 'Submitting…' : 'Submit request'}
              </Button>
            ) : (
              <Button size="lg" onClick={goNext} className="w-full sm:w-auto">
                Next
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------
function StepIndicator({ current }: { current: number }) {
  return (
    <ol className="flex flex-wrap items-center gap-2">
      {wizardSteps.map((s, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={s.id} className="flex items-center gap-2">
            <span
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                active
                  ? 'bg-primary text-primary-foreground'
                  : done
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted text-muted-foreground',
              )}
            >
              {done ? <Icon name="heroicons/check-micro" className="size-3.5" /> : i + 1}
            </span>
            <span
              className={cn(
                'hidden text-xs font-medium sm:inline',
                active ? 'text-foreground' : 'text-muted-foreground',
              )}
            >
              {s.shortTitle}
            </span>
            {i < wizardSteps.length - 1 ? <span className="bg-border mx-1 h-px w-4 sm:w-8" /> : null}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1 — Your property
// ---------------------------------------------------------------------------
function PropertyStep({
  draft,
  errors,
  onChange,
}: {
  draft: AppraisalDraft;
  errors: Errors;
  onChange: (patch: Partial<AppraisalDraft['property']>) => void;
}) {
  const mapsState = useGoogleMaps();
  const containerRef = useRef<HTMLDivElement>(null);
  // Guaranteed fallback for addresses the autocomplete dropdown never suggests
  // (e.g. rural South Burnett properties) — lets the user type freely instead
  // of relying on the autocomplete element's undocumented keystroke behaviour.
  const [manualEntry, setManualEntry] = useState(false);
  // Keep the latest onChange without re-initialising the autocomplete element.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const container = containerRef.current;
    if (mapsState !== 'ready' || manualEntry || !container || !window.google?.maps?.places?.PlaceAutocompleteElement)
      return;

    const autocomplete = new window.google.maps.places.PlaceAutocompleteElement({
      includedRegionCodes: ['au'],
    });
    autocomplete.id = 'address';
    autocomplete.className = 'address-autocomplete pl-9';
    autocomplete.setAttribute('placeholder', 'Start typing your address');
    autocomplete.setAttribute('aria-label', 'Property address');
    autocomplete.setAttribute('no-input-icon', '');
    (autocomplete as unknown as { value?: string }).value = draft.property.address;

    const handleSelect = async ({ placePrediction }: { placePrediction: GooglePlacePrediction }) => {
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress'] });
      if (place.formattedAddress) onChangeRef.current({ address: place.formattedAddress });
    };
    autocomplete.addEventListener('gmp-select', handleSelect);

    // Best-effort: mirror text the user typed but never picked a suggestion
    // for (e.g. a rural address Google doesn't have). Not officially
    // documented — the element's internal <input> should still dispatch
    // standard composed input/change events, but if it doesn't this just
    // silently no-ops rather than breaking anything.
    const syncTyped = (event: Event) => {
      const value = (event.target as HTMLInputElement | null)?.value;
      if (typeof value === 'string') onChangeRef.current({ address: value });
    };
    autocomplete.addEventListener('input', syncTyped);
    autocomplete.addEventListener('change', syncTyped);

    container.appendChild(autocomplete);

    return () => {
      autocomplete.removeEventListener('gmp-select', handleSelect);
      autocomplete.removeEventListener('input', syncTyped);
      autocomplete.removeEventListener('change', syncTyped);
      container.removeChild(autocomplete);
    };
    // Only (re-)mount when maps becomes ready or manual entry is toggled off —
    // draft.property.address is read once, at mount, purely to seed the
    // element's initial value (carrying over anything typed in manual mode).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsState, manualEntry]);

  const showAutocomplete = mapsState === 'ready' && !manualEntry;

  return (
    <div className="space-y-5">
      <Field label="Property address" htmlFor="address" error={errors['property.address']}>
        <div className="relative">
          <Icon
            name="heroicons/map-pin-micro"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 z-10 size-4 -translate-y-1/2"
          />
          {showAutocomplete ? (
            <div ref={containerRef} />
          ) : (
            <Input
              id="address"
              autoComplete="off"
              placeholder="Start typing your address"
              className="pl-9"
              aria-invalid={Boolean(errors['property.address'])}
              value={draft.property.address}
              onChange={(e) => onChange({ address: e.target.value })}
            />
          )}
        </div>
        {mapsState === 'no-key' || mapsState === 'error' ? (
          <p className="text-muted-foreground mt-1.5 text-xs">
            Address lookup isn't available right now — type the full address including suburb, state and postcode.
          </p>
        ) : null}
        {mapsState === 'ready' ? (
          <button
            type="button"
            onClick={() => setManualEntry((current) => !current)}
            className="text-muted-foreground hover:text-foreground mt-1.5 text-xs underline underline-offset-2"
          >
            {manualEntry ? 'Search for address instead' : "Can't find your address? Enter it manually"}
          </button>
        ) : null}
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Property details
// ---------------------------------------------------------------------------
function DetailsStep({
  draft,
  errors,
  onChange,
}: {
  draft: AppraisalDraft;
  errors: Errors;
  onChange: (patch: Partial<AppraisalDraft['property']>) => void;
}) {
  const showLandSize = draft.property.propertyType !== 'apartment' && draft.property.propertyType !== '';

  return (
    <div className="space-y-5">
      <Field label="Property type" htmlFor="propertyType" error={errors['property.propertyType']}>
        <Select
          value={draft.property.propertyType}
          onValueChange={(value) => onChange({ propertyType: value, ...(value === 'apartment' ? { landSizeSqm: '' } : {}) })}
        >
          <SelectTrigger id="propertyType" aria-invalid={Boolean(errors['property.propertyType'])}>
            <SelectValue placeholder="Select property type" />
          </SelectTrigger>
          <SelectContent>
            {propertyTypes.map((type) => (
              <SelectItem key={type.id} value={type.id}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Bedrooms" htmlFor="bedrooms" error={errors['property.bedrooms']}>
          <Input
            id="bedrooms"
            type="number"
            inputMode="numeric"
            min="0"
            aria-invalid={Boolean(errors['property.bedrooms'])}
            value={draft.property.bedrooms}
            onChange={(e) => onChange({ bedrooms: e.target.value })}
          />
        </Field>
        <Field label="Bathrooms" htmlFor="bathrooms" error={errors['property.bathrooms']}>
          <Input
            id="bathrooms"
            type="number"
            inputMode="numeric"
            min="0"
            aria-invalid={Boolean(errors['property.bathrooms'])}
            value={draft.property.bathrooms}
            onChange={(e) => onChange({ bathrooms: e.target.value })}
          />
        </Field>
        <Field label="Car spaces" htmlFor="carSpaces" error={errors['property.carSpaces']}>
          <Input
            id="carSpaces"
            type="number"
            inputMode="numeric"
            min="0"
            aria-invalid={Boolean(errors['property.carSpaces'])}
            value={draft.property.carSpaces}
            onChange={(e) => onChange({ carSpaces: e.target.value })}
          />
        </Field>
      </div>

      {showLandSize ? (
        <Field label="Land size (sqm)" htmlFor="landSizeSqm" optional error={errors['property.landSizeSqm']}>
          <Input
            id="landSizeSqm"
            type="number"
            inputMode="numeric"
            min="0"
            aria-invalid={Boolean(errors['property.landSizeSqm'])}
            value={draft.property.landSizeSqm}
            onChange={(e) => onChange({ landSizeSqm: e.target.value })}
          />
        </Field>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Reason & timeline
// ---------------------------------------------------------------------------
function TimelineStep({
  draft,
  errors,
  onChange,
}: {
  draft: AppraisalDraft;
  errors: Errors;
  onChange: (patch: Partial<AppraisalDraft['timeline']>) => void;
}) {
  return (
    <div className="space-y-5">
      <Field label="Reason for this appraisal" htmlFor="reason" error={errors['timeline.reason']}>
        <Select value={draft.timeline.reason} onValueChange={(value) => onChange({ reason: value })}>
          <SelectTrigger id="reason" aria-invalid={Boolean(errors['timeline.reason'])}>
            <SelectValue placeholder="Select a reason" />
          </SelectTrigger>
          <SelectContent>
            {sellingReasons.map((reason) => (
              <SelectItem key={reason.id} value={reason.id}>
                {reason.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      <Field label="Appraisal type" error={errors['timeline.appraisalType']}>
        <div className="grid gap-3 sm:grid-cols-2">
          {appraisalTypes.map((type) => (
            <OptionCard
              key={type.id}
              name="appraisalType"
              title={type.name}
              description={type.description}
              checked={draft.timeline.appraisalType === type.id}
              onSelect={() => onChange({ appraisalType: type.id as AppraisalType })}
            />
          ))}
        </div>
      </Field>

      <Field label="Ideal timeframe to sell" htmlFor="timeframe" error={errors['timeline.timeframe']}>
        <Select value={draft.timeline.timeframe} onValueChange={(value) => onChange({ timeframe: value })}>
          <SelectTrigger id="timeframe" aria-invalid={Boolean(errors['timeline.timeframe'])}>
            <SelectValue placeholder="Select a timeframe" />
          </SelectTrigger>
          <SelectContent>
            {timeframes.map((tf) => (
              <SelectItem key={tf.id} value={tf.id}>
                {tf.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Tell us more
// ---------------------------------------------------------------------------
function MoreInfoStep({
  draft,
  photoFiles,
  previewMap,
  fileInputRef,
  onNotesChange,
  onPhotosSelected,
  onRemovePhoto,
}: {
  draft: AppraisalDraft;
  photoFiles: Array<File>;
  previewMap: Map<File, string>;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onNotesChange: (notes: string) => void;
  onPhotosSelected: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemovePhoto: (index: number) => void;
}) {
  return (
    <div className="space-y-5">
      <Field label="Anything else we should know?" htmlFor="notes" optional>
        <Textarea
          id="notes"
          rows={4}
          placeholder="Recent renovations, unique features, anything that could affect your property's value…"
          value={draft.notes}
          onChange={(e) => onNotesChange(e.target.value)}
        />
      </Field>

      <Field label="Photos" htmlFor="photos" optional>
        <input
          id="photos"
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="text-muted-foreground file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
          onChange={onPhotosSelected}
        />
        <p className="text-muted-foreground mt-1.5 text-xs">
          Up to {MAX_PHOTOS} images, {MAX_PHOTO_BYTES / (1024 * 1024)} MB each.
        </p>

        {photoFiles.length > 0 ? (
          <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
            {photoFiles.map((file, index) => (
              <li key={`${file.name}-${file.size}-${file.lastModified}`} className="group relative">
                <img
                  src={previewMap.get(file)}
                  alt={file.name}
                  className="border-input aspect-square w-full rounded-md border object-cover"
                />
                <button
                  type="button"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => onRemovePhoto(index)}
                  className="bg-background/90 text-muted-foreground hover:text-destructive absolute top-1 right-1 flex size-6 items-center justify-center rounded-full border shadow-xs"
                >
                  <Icon name="heroicons/trash-micro" className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5 — Your details
// ---------------------------------------------------------------------------
function ContactStep({
  draft,
  errors,
  onChange,
}: {
  draft: AppraisalDraft;
  errors: Errors;
  onChange: (patch: Partial<AppraisalDraft['contact']>) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="First name" htmlFor="firstName" error={errors['contact.firstName']}>
          <Input
            id="firstName"
            aria-invalid={Boolean(errors['contact.firstName'])}
            value={draft.contact.firstName}
            onChange={(e) => onChange({ firstName: e.target.value })}
          />
        </Field>
        <Field label="Last name" htmlFor="lastName" error={errors['contact.lastName']}>
          <Input
            id="lastName"
            aria-invalid={Boolean(errors['contact.lastName'])}
            value={draft.contact.lastName}
            onChange={(e) => onChange({ lastName: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email" htmlFor="email" error={errors['contact.email']}>
          <Input
            id="email"
            type="email"
            aria-invalid={Boolean(errors['contact.email'])}
            value={draft.contact.email}
            onChange={(e) => onChange({ email: e.target.value })}
          />
        </Field>
        <Field label="Phone" htmlFor="phone" error={errors['contact.phone']}>
          <Input
            id="phone"
            type="tel"
            aria-invalid={Boolean(errors['contact.phone'])}
            value={draft.contact.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
          />
        </Field>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Preferred contact method" htmlFor="preferredMethod" error={errors['contact.preferredMethod']}>
          <Select
            value={draft.contact.preferredMethod}
            onValueChange={(value) => onChange({ preferredMethod: value })}
          >
            <SelectTrigger id="preferredMethod" aria-invalid={Boolean(errors['contact.preferredMethod'])}>
              <SelectValue placeholder="Select a method" />
            </SelectTrigger>
            <SelectContent>
              {contactMethods.map((method) => (
                <SelectItem key={method.id} value={method.id}>
                  {method.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Best time to contact you" htmlFor="preferredTime" error={errors['contact.preferredTime']}>
          <Select value={draft.contact.preferredTime} onValueChange={(value) => onChange({ preferredTime: value })}>
            <SelectTrigger id="preferredTime" aria-invalid={Boolean(errors['contact.preferredTime'])}>
              <SelectValue placeholder="Select a time" />
            </SelectTrigger>
            <SelectContent>
              {contactTimes.map((time) => (
                <SelectItem key={time.id} value={time.id}>
                  {time.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div>
        <label className="flex items-start gap-2.5">
          <Checkbox
            checked={draft.contact.consent}
            onCheckedChange={(checked) => onChange({ consent: checked === true })}
            aria-invalid={Boolean(errors['contact.consent'])}
            className="mt-0.5"
          />
          <span className="text-sm">I agree to be contacted about my property appraisal.</span>
        </label>
        {errors['contact.consent'] ? (
          <p className="text-destructive mt-1 text-xs">{errors['contact.consent']}</p>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 6 — Review
// ---------------------------------------------------------------------------
function ReviewStep({ draft, onEditStep }: { draft: AppraisalDraft; onEditStep: (step: number) => void }) {
  const propertyTypeName = propertyTypes.find((t) => t.id === draft.property.propertyType)?.name ?? '—';
  const reasonName = sellingReasons.find((r) => r.id === draft.timeline.reason)?.name ?? '—';
  const appraisalTypeName = appraisalTypes.find((t) => t.id === draft.timeline.appraisalType)?.name ?? '—';
  const timeframeName = timeframes.find((t) => t.id === draft.timeline.timeframe)?.name ?? '—';
  const contactMethodName = contactMethods.find((m) => m.id === draft.contact.preferredMethod)?.name ?? '—';
  const contactTimeName = contactTimes.find((t) => t.id === draft.contact.preferredTime)?.name ?? '—';

  return (
    <div className="space-y-4">
      <ReviewSection title="Your property" onEdit={() => onEditStep(0)}>
        <SummaryRow label="Address" value={draft.property.address} />
      </ReviewSection>

      <ReviewSection title="Property details" onEdit={() => onEditStep(1)}>
        <SummaryRow label="Type" value={propertyTypeName} />
        <SummaryRow
          label="Bed / bath / car"
          value={`${draft.property.bedrooms || '—'} / ${draft.property.bathrooms || '—'} / ${draft.property.carSpaces || '—'}`}
        />
        {draft.property.landSizeSqm ? <SummaryRow label="Land size" value={`${draft.property.landSizeSqm} sqm`} /> : null}
      </ReviewSection>

      <ReviewSection title="Reason & timeline" onEdit={() => onEditStep(2)}>
        <SummaryRow label="Reason" value={reasonName} />
        <SummaryRow label="Appraisal type" value={appraisalTypeName} />
        <SummaryRow label="Timeframe" value={timeframeName} />
      </ReviewSection>

      <ReviewSection title="More information" onEdit={() => onEditStep(3)}>
        <SummaryRow label="Notes" value={draft.notes || '—'} />
        <SummaryRow label="Photos" value={draft.photos.length > 0 ? `${draft.photos.length} file(s)` : '—'} />
      </ReviewSection>

      <ReviewSection title="Your details" onEdit={() => onEditStep(4)}>
        <SummaryRow label="Name" value={`${draft.contact.firstName} ${draft.contact.lastName}`.trim()} />
        <SummaryRow label="Email" value={draft.contact.email} />
        <SummaryRow label="Phone" value={draft.contact.phone} />
        <SummaryRow label="Contact" value={`${contactMethodName} · ${contactTimeName}`} />
      </ReviewSection>

      <div className="bg-muted rounded-lg p-4">
        <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
          {APPRAISAL_DISCLAIMER.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ReviewSection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border-input rounded-lg border p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold">{title}</span>
        <button type="button" onClick={onEdit} className="text-primary text-xs font-medium hover:underline">
          Edit
        </button>
      </div>
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <dt className="text-muted-foreground w-32 shrink-0">{label}</dt>
      <dd className="min-w-0 break-words">{value || '—'}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confirmation screen (after successful submit)
// ---------------------------------------------------------------------------
function Confirmation() {
  return (
    <div className="py-12 text-center">
      <div className="bg-primary/10 text-primary mx-auto mb-6 flex size-14 items-center justify-center rounded-full">
        <Icon name="heroicons/check-circle-mini" className="size-7" />
      </div>
      <h2 className="mb-3 text-2xl font-semibold">Request received</h2>
      <p className="text-muted-foreground mb-8 text-sm leading-relaxed">
        Thanks — we've received your appraisal request. One of our agents will review the details and be in touch
        using your preferred contact method.
      </p>
      <Button asChild size="lg">
        <Link to="/">Back to home</Link>
      </Button>
    </div>
  );
}
